import { get, isEmpty, set } from 'lodash';
import { join } from 'lodash/array';
import map from 'lodash/map';
import size from 'lodash/size';
import {
  GET_ACTIVITIES_ENDPOINT,
  GET_EMPLOYEES_FOR_SURVEY_ENDPOINT,
  GET_PROJECT_DOCUMENTS,
  GET_VENDORS_FOR_SURVEY_ENDPOINT,
  SEARCH_POPULATION_ENDPOINT,
  SEARCH_PROJECTS_QNQ_ENDPOINT,
  VALARI_API_NAME,
} from '../../../../../config/api_endpoints';
import postWithContext from '../../../../../utils/api_util';
import { ITEM_TYPE_LABOR } from '../../../../../config/api_constants';
import { getSerializedData } from '../../../../table/utils/table_redux_payload_utils';

export const SURVEY_TYPE = {
  LABOR: 'Labor',
  CONTRACT: 'Contract',
  LABOR_DUAL: 'LaborDual',
  LABOR_BC_SUBSTANTIATION: 'LaborBCSubstantiation',
  LABOR_PROJECT_SUBSTANTIATION: 'LaborProjectSubstantiation',
};

const parser = data => {
  try {
    return JSON.parse(data);
  } catch (e) {
    return null;
  }
};

export const getSurveyType = data => {
  if (get(data, 'surveyType', ' ') === SURVEY_TYPE.CONTRACT) {
    return SURVEY_TYPE.CONTRACT;
  }
  if (get(data, 'surveyType', ' ') === SURVEY_TYPE.LABOR_DUAL) {
    return SURVEY_TYPE.LABOR_DUAL;
  }
  if (get(data, 'surveyType', ' ') === SURVEY_TYPE.LABOR_BC_SUBSTANTIATION) {
    return SURVEY_TYPE.LABOR_BC_SUBSTANTIATION;
  }
  if (get(data, 'surveyType', ' ') === SURVEY_TYPE.LABOR_PROJECT_SUBSTANTIATION) {
    return SURVEY_TYPE.LABOR_PROJECT_SUBSTANTIATION;
  }
  return SURVEY_TYPE.LABOR;
};

export const getProjectDescription = (data, surveyType) => {
  switch (surveyType) {
    case SURVEY_TYPE.LABOR_DUAL:
      return (
        'The table below shows the total percentage ' +
        'of time that each employee worked on this Business ' +
        'Component. This percentage is derived from the ' +
        'allocations of each employee to projects that was ' +
        'collected in the earlier R&D Study survey. ' +
        'Please review the total employee time allocated ' +
        'to this Business Component and if needed, you ' +
        'can make changes to these percentage allocations.'
      );

    case SURVEY_TYPE.CONTRACT:
      return `Select all projects vendors worked on in 
      ${data.studyPeriod}. You can add project(s) if you
       cannot find in the list.`;

    case SURVEY_TYPE.LABOR:
      return `Select all projects you and/or your 
      employees worked on in ${data.studyPeriod}. 
      You can add project(s) if you cannot find your 
      team's project(s) in the list.`;

    default:
      return `Select all projects you and/or your
       employees worked on in ${data.studyPeriod}. 
       You can add project(s) if you cannot find your 
       team's project(s) in the list.`;
  }
};

export const getActivityDescription = (data, surveyType) => {
  switch (surveyType) {
    case SURVEY_TYPE.LABOR:
    default:
      return `Select all activities you and/or your employees worked 
      on in ${data.studyPeriod}. You can add activities if you 
      cannot find your team’s activities in the list.`;

    case SURVEY_TYPE.CONTRACT:
      return `Select all activities vendors worked on in ${data.studyPeriod}.
         You can add activities if you cannot find in the list.`;
  }
};

const transformReceivedEmployees = ({ employeeAlias, employeeName, jobTitle }) => {
  return { employeeAlias, employeeName, jobTitle };
};

const getSecondaryAssigneeDetails = async (employees, studyPeriod) => {
  let secondaryAssigneeDetails = [];
  const query = join(
    map(employees, employee => {
      return `recordId equals ${employee}`;
    }),
    ' || ',
  );

  if (size(employees) > 0) {
    await postWithContext(VALARI_API_NAME, SEARCH_POPULATION_ENDPOINT, {
      body: {
        itemType: ITEM_TYPE_LABOR,
        pageSize: 20,
        pageNumber: 1,
        query,
        studyPeriod,
      },
    }).then(r => {
      secondaryAssigneeDetails = map(getSerializedData(get(r, 'body.data', [])), res =>
        transformReceivedEmployees(res),
      );
    });
  }

  return secondaryAssigneeDetails;
};

const getAdditionalSurveyData = async (item, searchProjectQuery, entityId) => {
  const promises = [
    postWithContext(
      VALARI_API_NAME,
      GET_ACTIVITIES_ENDPOINT,
      {
        body: {
          surveyType: item.surveyType,
          studyPeriod: item.studyPeriod,
        },
      },
      entityId,
    ),
    postWithContext(
      VALARI_API_NAME,
      GET_EMPLOYEES_FOR_SURVEY_ENDPOINT,
      {
        body: {
          surveyId: item.surveyId,
          studyPeriod: item.studyPeriod,
        },
      },
      entityId,
    ),
    postWithContext(
      VALARI_API_NAME,
      GET_VENDORS_FOR_SURVEY_ENDPOINT,
      {
        body: {
          surveyId: item.surveyId,
          studyPeriod: item.studyPeriod,
        },
      },
      entityId,
    ),
    postWithContext(
      VALARI_API_NAME,
      GET_PROJECT_DOCUMENTS,
      {
        body: {
          projectIdList: map(
            get(parser(get(item, 'userResponse', '{}')), 'Project.data.selectedProjects', []),
            ({ id }) => id,
          ),
          surveyId: item.surveyId,
          studyPeriod: item.studyPeriod,
        },
      },
      entityId,
    ),
  ];
  if (!isEmpty(searchProjectQuery)) {
    promises.push(
      postWithContext(VALARI_API_NAME, SEARCH_PROJECTS_QNQ_ENDPOINT, {
        body: {
          studyPeriod: item.studyPeriod,
          query: searchProjectQuery,
        },
      }),
    );
  }
  const response = await Promise.all(promises)
    .then(r => {
      return r;
    })
    .catch(() => {
      return [];
    });

  return response;
};

const parseProjectData = data => {
  const projectData = {};
  JSON.parse(data).forEach(project => {
    set(projectData, project.projectId, project);
  });
  return projectData;
};

const getSurveyData = async (item, entityId) => {
  const ddbUserResponse = parser(get(item, 'userResponse', {}));
  const s3UserResponse = JSON.parse(get(ddbUserResponse, 'userResponse', '{}'));
  const userResponse = { ...ddbUserResponse, ...s3UserResponse };
  const selectedProjects = get(userResponse, 'Project.data.selectedProjects', []);
  let searchProjectQuery = '';
  selectedProjects.forEach(({ id }) => {
    searchProjectQuery += `projectId equals ${id} || `;
  });
  const response = await getAdditionalSurveyData(item, searchProjectQuery, entityId);
  const secondaryAssigneeDetails = await getSecondaryAssigneeDetails(
    getSerializedData(get(item, 'serializedSecondaryAssignees', [])),
    item.studyPeriod,
  );
  if (isEmpty(response)) {
    return null;
  }
  // Check if survey is labor dual
  const isLaborDual = item.surveyType === SURVEY_TYPE.LABOR_DUAL;
  const result = {
    ...item,
    activityList: get(response, [0, 'body', 'activityList'], []),
    userResponse,
    employeeData: parser(get(response, [1, 'employeeData'], [])),
    vendorData: parser(get(response, [2, 'vendorData'], [])),
    projectDocumentation: parser(get(response[3], 'body.projectIdToDocumentDataMap', '{}')),
    secondaryAssigneeDetails,
    searchProjectsResponse: parseProjectData(get(response, [4, 'body', 'data'], '[]')),
  };

  // Add new format fields if they exist
  if (isLaborDual) {
    result.activityDualAllocation = get(userResponse, 'ActivityDualAllocation.data', {});
    result.projectAllocationBreakdown = get(userResponse, 'ProjectAllocationBreakdown.data', {});
    result.activityAllocationBreakdown = get(userResponse, 'ActivityAllocationBreakdown.data', {});
  }

  return result;
};

export default getSurveyData;
